import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IAdMediaDimensions, adMediaDimensions } from '@x/common/ads/ad-media.config';
import { TAdPlacement } from '@x/common/ads/ad.config';
import { CloudinaryModule } from '@x/common/cloudinary';
import { MediaModule } from '@x/common/media';
import { DeviceSizes, ResponsiveService } from '@x/common/responsive';
import { TShopAdMedia } from '@x/ecommerce/shop-client';
import { BehaviorSubject, Observable, withLatestFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';

interface IShopAdVideo {
  url: string;
  dimensions: IAdMediaDimensions;
}

@Component({
  selector: 'x-ad-video',
  standalone: true,
  imports: [AsyncPipe, CloudinaryModule, MediaModule],
  templateUrl: './ad-video.component.html',
  styleUrl: './ad-video.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdVideoComponent {
  private _videos = new BehaviorSubject<TShopAdMedia[]>([]);

  @Input() set media(media: TShopAdMedia[]) {
    const videos = media.filter((m) => m.type === 'video');
    this._videos.next(videos);
  }

  responsiveVideo$: Observable<IShopAdVideo | null> = this.responsiveService
    .observeBreakpoints()
    .pipe(
      map(({ size }) => size),
      filter(Boolean),
      map((size) => {
        const largeSize: DeviceSizes[] = ['LG', 'XL', 'XXL'];
        return largeSize.includes(size) ? 'LG' : size;
      }),
      withLatestFrom(this._videos),
      map(([size, videos]) => {
        const rVideo = videos.find((v) => (v.mediaQuery.toUpperCase() as DeviceSizes) === size);

        if (!rVideo) return null;

        const placement = rVideo.placement;

        if (!placement) return null;

        return {
          url: rVideo.url,
          dimensions: adMediaDimensions(size as DeviceSizes, placement as TAdPlacement),
        };
      }),
    );

  constructor(private responsiveService: ResponsiveService) {}
}
